import { weekday } from '@/features/menu/utils/menu-draft-text';
import type { Translations } from '@/i18n/locales/en';
import { formatMoney } from '@/utils/money';
import { bpsToPercent } from '@/utils/percent';
import type { Promotion } from '@restaurant/api-client';
import type { PromotionConflict, PromotionValue } from '@restaurant/api-contracts';
import { DateTime } from 'luxon';

type PromotionsT = Translations['promotions'];
type QuickEditT = Translations['promotionsQuickEdit'];

export const valueOfPromotion = (promotion: Promotion): PromotionValue => ({
  name: promotion.name,
  description: promotion.description,
  discountType: promotion.discountType,
  discountValue: promotion.discountValue,
  maxDiscountCents: promotion.maxDiscountCents,
  targetType: promotion.targetType,
  categoryIds: promotion.targets.flatMap((target) =>
    target.categoryId ? [target.categoryId] : [],
  ),
  productIds: promotion.targets.flatMap((target) => (target.productId ? [target.productId] : [])),
  minSubtotalCents: promotion.minSubtotalCents,
  orderTypes: promotion.orderTypes,
  paymentMethodKinds: promotion.paymentMethodKinds,
  startsAt: promotion.startsAt,
  endsAt: promotion.endsAt,
  weeklySchedule: promotion.weeklySchedule,
  stackable: promotion.stackable,
  priority: promotion.priority,
  trigger: promotion.trigger,
  couponKind: promotion.couponKind,
  customerVisible: promotion.customerVisible,
  code: promotion.code
    ? {
        code: promotion.code.code,
        maxRedemptions: promotion.code.maxRedemptions,
        oncePerCustomer: promotion.code.oncePerCustomer,
      }
    : null,
});

// Map the raw enum to the localized labels ("Pickup"/"Delivery"), matching
// the edit page - never dump the SCREAMING_CASE enum into the summary.
export const orderTypeLabel = (type: PromotionValue['orderTypes'][number], tp: PromotionsT) => {
  const labels: Record<string, string> = {
    DINE_IN: tp.orderType.dineIn,
    PICKUP: tp.orderType.pickup,
    DELIVERY: tp.orderType.delivery,
  };
  return labels[type] ?? type;
};

function discountLabel(value: PromotionValue, currency: string, tp: PromotionsT): string {
  switch (value.discountType) {
    case 'PERCENTAGE':
      return tp.summaryPercent(bpsToPercent(value.discountValue));
    case 'FIXED_AMOUNT':
      return tp.summaryAmount(formatMoney(value.discountValue, currency));
    case 'FREE_DELIVERY':
      return tp.discountType.freeDelivery;
  }
}

function targetLabel(value: PromotionValue, tp: PromotionsT): string {
  if (value.targetType === 'ENTIRE_ORDER') return tp.summaryWholeOrder;
  return [
    value.categoryIds.length > 0 ? tp.summaryCategories(value.categoryIds.length) : null,
    value.productIds.length > 0 ? tp.summaryProducts(value.productIds.length) : null,
  ]
    .filter((part) => part !== null)
    .join(', ');
}

function paymentMethodSummary(value: PromotionValue, tp: PromotionsT): string | null {
  const kinds = value.paymentMethodKinds;
  if (kinds.length === 0) return null;
  if (kinds.length === 1 && kinds[0] === 'CASH') return tp.summaryCashOnly;
  const labels: Record<string, string> = {
    CASH: tp.paymentMethod.cash,
    CARD_ON_HANDOFF: tp.paymentMethod.cardOnHandoff,
    ONLINE: tp.paymentMethod.online,
    MANUAL: tp.paymentMethod.manual,
  };
  return kinds.map((kind) => labels[kind] ?? kind).join(', ');
}

export function promotionSummary(value: PromotionValue, currency: string, tp: PromotionsT): string {
  const parts = [`${discountLabel(value, currency, tp)} · ${targetLabel(value, tp)}`];
  if (value.orderTypes.length > 0) {
    parts.push(value.orderTypes.map((type) => orderTypeLabel(type, tp)).join(', '));
  }
  const payment = paymentMethodSummary(value, tp);
  if (payment) parts.push(payment);
  return parts.join(' · ');
}

export function promotionWhen(
  value: PromotionValue,
  timezone: string,
  locale: string,
  tp: PromotionsT,
): string {
  const parts: string[] = [];
  if (value.weeklySchedule) {
    const byHours = new Map<string, number[]>();
    for (const window of value.weeklySchedule) {
      const hours = `${window.start}–${window.end}`;
      byHours.set(hours, [...(byHours.get(hours) ?? []), window.dayOfWeek]);
    }
    for (const [hours, days] of byHours) {
      const mondayFirst = [...days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));
      parts.push(`${mondayFirst.map((day) => weekday(day, locale)).join(', ')} ${hours}`);
    }
  }
  const date = (iso: string, wholeDay: string) => {
    const at = DateTime.fromISO(iso, { zone: 'utc' }).setZone(timezone).setLocale(locale);
    const day = at.toFormat(
      at.year === DateTime.now().setZone(timezone).year ? 'd LLL' : 'd LLL yyyy',
    );
    return at.toFormat('HH:mm') === wholeDay ? day : `${day} ${at.toFormat('HH:mm')}`;
  };
  const from = (iso: string) => date(iso, '00:00');
  const until = (iso: string) => date(iso, '23:59');
  if (value.startsAt && value.endsAt)
    parts.push(`${from(value.startsAt)} – ${until(value.endsAt)}`);
  else if (value.startsAt) parts.push(`${tp.starts} ${from(value.startsAt)}`);
  else if (value.endsAt) parts.push(`${tp.ends} ${until(value.endsAt)}`);
  if (value.code) parts.push(tp.codeDisplay(value.code.code));
  return parts.join(' · ');
}

export function conflictText(
  conflict: PromotionConflict,
  otherName: string,
  context: { currency: string; timezone: string; locale: string },
  tp: PromotionsT,
  tq: QuickEditT,
): string {
  const when = DateTime.fromISO(conflict.at, { zone: 'utc' })
    .setZone(context.timezone)
    .setLocale(context.locale)
    .toFormat('ccc HH:mm');
  const example = `${when} · ${orderTypeLabel(conflict.orderType, tp)} · ${formatMoney(conflict.subtotalCents, context.currency)}`;
  return tq.conflict[conflict.effect](example, otherName);
}
